import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

const DealerDashboard = () => {
  const { dealer, dealerToken, dealerLogout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [photographers, setPhotographers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // New photographer form
  const [newPhoto, setNewPhoto] = useState({ name: '', email: '', mobile: '', password: '' });
  const [newPhotoError, setNewPhotoError] = useState('');
  const [newPhotoSuccess, setNewPhotoSuccess] = useState('');
  const [newPhotoLoading, setNewPhotoLoading] = useState(false);
  // Assign quota
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignAmount, setAssignAmount] = useState('');
  const [assignMsg, setAssignMsg] = useState('');
  // Payment
  const [payLoading, setPayLoading] = useState(null);
  // Events
  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [eventForm, setEventForm] = useState({ name: '', date: '', venue: '', description: '' });
  const [eventCreating, setEventCreating] = useState(false);
  const [eventError, setEventError] = useState('');

  const authHeaders = { headers: { Authorization: `Bearer ${dealerToken}` } };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dealer/dashboard`, authHeaders);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) { dealerLogout(); navigate('/login'); }
      else setError('Failed to load dashboard.');
    } finally { setLoading(false); }
  }, [dealerToken]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  useEffect(() => {
    if (tab === 'photographers' && photographers.length === 0) {
      axios.get(`${API_URL}/api/dealer/photographers`, authHeaders).then(r => setPhotographers(r.data)).catch(() => {});
    }
    if (tab === 'buy' && packages.length === 0) {
      axios.get(`${API_URL}/api/packages?type=dealer`).then(r => setPackages(r.data)).catch(() => {});
    }
    if (tab === 'ledger' && !ledger) {
      axios.get(`${API_URL}/api/dealer/ledger`, authHeaders).then(r => setLedger(r.data)).catch(() => {});
    }
    if (tab === 'events' && !eventsLoaded) {
      axios.get(`${API_URL}/api/dealer/events`, authHeaders).then(r => { setEvents(r.data); setEventsLoaded(true); }).catch(() => {});
    }
  }, [tab]);

  const createPhotographer = async () => {
    if (!newPhoto.name || !newPhoto.email || !newPhoto.password) { setNewPhotoError('Name, email and password are required.'); return; }
    setNewPhotoError(''); setNewPhotoSuccess(''); setNewPhotoLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/dealer/photographers`, newPhoto, authHeaders);
      setNewPhotoSuccess(`Account created for ${res.data.name}. Share credentials: ${newPhoto.email} / ${newPhoto.password}`);
      setNewPhoto({ name: '', email: '', mobile: '', password: '' });
      setPhotographers(prev => [...prev, res.data]);
    } catch (err) { setNewPhotoError(err.response?.data?.error || 'Failed.'); }
    finally { setNewPhotoLoading(false); }
  };

  const assignQuota = async (photographerId) => {
    const events = parseInt(assignAmount);
    if (!events || events < 1) { setAssignMsg('Enter a valid number.'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/dealer/photographers/${photographerId}/assign-quota`, { events }, authHeaders);
      setAssignMsg(res.data.message);
      setAssignTarget(null); setAssignAmount('');
      loadDashboard();
      setPhotographers(prev => prev.map(p => p.id === photographerId ? { ...p, remaining_quota: res.data.photographer_quota } : p));
    } catch (err) { setAssignMsg(err.response?.data?.error || 'Failed.'); }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    setEventError(''); setEventCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/dealer/events`, eventForm, authHeaders);
      setEvents(prev => [res.data, ...prev]);
      setShowCreateEvent(false);
      setEventForm({ name: '', date: '', venue: '', description: '' });
      loadDashboard(); // refresh quota
    } catch (err) {
      setEventError(err.response?.data?.error || 'Failed to create event.');
      if (err.response?.data?.quota_empty) { setShowCreateEvent(false); setTab('buy'); }
    } finally { setEventCreating(false); }
  };

  const buyPackage = async (pkg) => {
    setPayLoading(pkg.id);
    try {
      const res = await axios.post(`${API_URL}/api/payments/create-order`, { package_id: pkg.id }, authHeaders);
      const { razorpay_order_id, amount, key_id, package_name, events_count } = res.data;

      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve; script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency: 'INR',
        order_id: razorpay_order_id,
        name: 'AnupLights Event Photos',
        description: `${package_name} — ${events_count} events`,
        prefill: { contact: dealer?.mobile_number, name: dealer?.name },
        handler: (response) => {
          // Payment captured — webhook will add quota
          // Show success and reload dashboard
          alert(`Payment successful! ${events_count} events will be added to your account shortly.`);
          loadDashboard();
        },
        modal: { ondismiss: () => setPayLoading(null) }
      });
      rzp.open();
    } catch (err) {
      alert(err.response?.data?.error || 'Payment initiation failed.');
    } finally { setPayLoading(null); }
  };

  if (loading) return <div style={s.page}><p style={{ color: '#fff' }}>Loading...</p></div>;

  const d = data?.dealer;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Dealer Dashboard</h1>
          <p style={s.subtitle}>Welcome, {d?.name} · {d?.city}</p>
        </div>
      </div>

      {/* Quick stats */}
      <div style={s.statsRow}>
        {[
          { label: 'Available Events', value: d?.remaining_quota ?? 0, color: '#10b981' },
          { label: 'Total Purchased', value: d?.total_purchased ?? 0, color: '#4f46e5' },
          { label: 'Assigned to Photographers', value: d?.total_assigned ?? 0, color: '#f59e0b' },
          { label: 'My Events', value: data?.event_count ?? 0, color: '#7c3aed' },
          { label: 'Photographers', value: data?.photographer_count ?? 0, color: '#0f3460' },
        ].map(stat => (
          <div key={stat.label} style={s.statCard}>
            <p style={{ ...s.statValue, color: stat.color }}>{stat.value}</p>
            <p style={s.statLabel}>{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {[['overview','Overview'],['events','My Events'],['photographers','My Photographers'],['buy','Buy Quota'],['ledger','Ledger']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ ...s.tab, ...(tab === key ? s.tabActive : {}) }}>{label}</button>
        ))}
      </div>

      <div style={s.content}>
        {/* OVERVIEW TAB */}
        {tab === 'overview' && (
          <div>
            <h3 style={s.sectionTitle}>Recent Transactions</h3>
            {data?.recent_transactions?.length === 0 && <p style={s.empty}>No transactions yet. Buy your first quota package.</p>}
            {data?.recent_transactions?.map(txn => (
              <div key={txn.id} style={s.txnRow}>
                <div>
                  <p style={s.txnTitle}>{txn.events_purchased} events</p>
                  <p style={s.txnMeta}>{new Date(txn.created_at).toLocaleDateString('en-IN')}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={s.txnAmount}>₹{(txn.amount_paise / 100).toFixed(0)}</p>
                  <span style={{ ...s.badge, background: txn.status === 'paid' ? '#d1fae5' : '#fef3c7', color: txn.status === 'paid' ? '#065f46' : '#92400e' }}>
                    {txn.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* EVENTS TAB */}
        {tab === 'events' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={s.sectionTitle}>My Events ({events.length})</h3>
              <button style={s.btn} onClick={() => { setEventError(''); setShowCreateEvent(true); }}>+ Create Event</button>
            </div>

            {/* Create Event Modal */}
            {showCreateEvent && (
              <div style={s.overlay} onClick={() => setShowCreateEvent(false)}>
                <div style={s.modal} onClick={e => e.stopPropagation()}>
                  <h3 style={{ margin: '0 0 1.25rem', fontWeight: 800, color: '#1a1a2e' }}>Create New Event</h3>
                  <form onSubmit={createEvent}>
                    {[
                      { label: 'Event Name *', key: 'name', type: 'text', placeholder: 'e.g. ABC Wedding' },
                      { label: 'Event Date *', key: 'date', type: 'date', placeholder: '' },
                      { label: 'Venue *', key: 'venue', type: 'text', placeholder: 'e.g. Grand Ballroom, Mumbai' },
                      { label: 'Description', key: 'description', type: 'text', placeholder: 'Optional' },
                    ].map(({ label, key, type, placeholder }) => (
                      <div key={key} style={{ marginBottom: '0.875rem' }}>
                        <label style={s.label}>{label}</label>
                        <input style={s.input} type={type} placeholder={placeholder} required={label.includes('*')}
                          value={eventForm[key]} onChange={e => setEventForm(f => ({ ...f, [key]: e.target.value }))} />
                      </div>
                    ))}
                    {eventError && <p style={s.errText}>{eventError}</p>}
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                      <button type="button" style={s.cancelBtn} onClick={() => setShowCreateEvent(false)}>Cancel</button>
                      <button type="submit" style={{ ...s.btn, opacity: eventCreating ? 0.6 : 1 }} disabled={eventCreating}>
                        {eventCreating ? 'Creating...' : 'Create Event'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {!eventsLoaded ? (
              <p style={s.empty}>Loading...</p>
            ) : events.length === 0 ? (
              <p style={s.empty}>No events yet. Create your first event.</p>
            ) : (
              events.map(ev => (
                <div key={ev.id} style={s.eventRow}>
                  <div>
                    <p style={s.txnTitle}>{ev.name}</p>
                    <p style={s.txnMeta}>
                      {new Date(ev.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {' · '}{ev.venue}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ margin: 0, fontWeight: 700, color: '#0f3460' }}>{ev.visitor_count}</p>
                      <p style={{ margin: 0, fontSize: '0.7rem', color: '#999' }}>visitors</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ margin: 0, fontWeight: 700, color: '#0f3460' }}>{ev.photo_count}</p>
                      <p style={{ margin: 0, fontSize: '0.7rem', color: '#999' }}>photos</p>
                    </div>
                    <span style={{ ...s.badge, background: ev.status === 'published' ? '#d1fae5' : '#dbeafe', color: ev.status === 'published' ? '#065f46' : '#1e40af' }}>
                      {ev.status}
                    </span>
                    <button style={s.smallBtn} onClick={() => navigate(`/dealer/events/${ev.id}`)}>Manage →</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* PHOTOGRAPHERS TAB */}
        {tab === 'photographers' && (
          <div>
            {/* Create new photographer */}
            <div style={s.section}>
              <h3 style={s.sectionTitle}>Create Photographer Account</h3>
              <div style={s.formGrid}>
                {[['name','Full Name','text'],['email','Email','email'],['mobile','Mobile (optional)','tel'],['password','Password','password']].map(([key,label,type]) => (
                  <div key={key} style={s.field}>
                    <label style={s.label}>{label}</label>
                    <input style={s.input} type={type} value={newPhoto[key]}
                      onChange={e => setNewPhoto(p => ({ ...p, [key]: e.target.value }))} />
                  </div>
                ))}
              </div>
              {newPhotoError && <p style={s.errText}>{newPhotoError}</p>}
              {newPhotoSuccess && <div style={s.successBox}>{newPhotoSuccess}</div>}
              <button style={{ ...s.btn, marginTop: '1rem' }} onClick={createPhotographer} disabled={newPhotoLoading}>
                {newPhotoLoading ? 'Creating...' : 'Create Account'}
              </button>
            </div>

            {/* Photographer list */}
            <h3 style={{ ...s.sectionTitle, marginTop: '2rem' }}>Photographers ({photographers.length})</h3>
            {photographers.length === 0 && <p style={s.empty}>No photographers added yet.</p>}
            {photographers.map(p => (
              <div key={p.id} style={s.photoRow}>
                <div>
                  <p style={s.photoName}>{p.name}</p>
                  <p style={s.photoMeta}>{p.email} {p.phone ? `· ${p.phone}` : ''}</p>
                </div>
                <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div>
                    <p style={s.quotaNum}>{p.remaining_quota}</p>
                    <p style={s.quotaLabel}>events left</p>
                  </div>
                  {assignTarget === p.id ? (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input style={{ ...s.input, width: 70, padding: '0.4rem 0.6rem' }} type="number" min="1"
                        value={assignAmount} onChange={e => setAssignAmount(e.target.value)} placeholder="N" />
                      <button style={s.smallBtn} onClick={() => assignQuota(p.id)}>Assign</button>
                      <button style={s.cancelBtn} onClick={() => { setAssignTarget(null); setAssignMsg(''); }}>✕</button>
                    </div>
                  ) : (
                    <button style={s.smallBtn} onClick={() => { setAssignTarget(p.id); setAssignMsg(''); }}>+ Assign Quota</button>
                  )}
                </div>
              </div>
            ))}
            {assignMsg && <p style={{ color: '#4f46e5', marginTop: '0.5rem', fontSize: '0.875rem' }}>{assignMsg}</p>}
          </div>
        )}

        {/* BUY QUOTA TAB */}
        {tab === 'buy' && (
          <div>
            <h3 style={s.sectionTitle}>Buy Event Quota</h3>
            <p style={s.empty}>Your available balance: <strong>{d?.remaining_quota} events</strong></p>
            <div style={s.packagesGrid}>
              {packages.map(pkg => (
                <div key={pkg.id} style={s.packageCard}>
                  <p style={s.pkgName}>{pkg.name}</p>
                  <p style={s.pkgEvents}>{pkg.events_count} Events</p>
                  <p style={s.pkgPrice}>₹{(pkg.price_paise / 100).toFixed(0)}</p>
                  <p style={s.pkgPer}>₹{Math.round(pkg.price_paise / pkg.events_count / 100)}/event</p>
                  <button style={{ ...s.btn, marginTop: '1rem' }}
                    onClick={() => buyPackage(pkg)}
                    disabled={payLoading === pkg.id}>
                    {payLoading === pkg.id ? 'Processing...' : 'Buy Now'}
                  </button>
                </div>
              ))}
              {packages.length === 0 && <p style={s.empty}>No packages available.</p>}
            </div>
          </div>
        )}

        {/* LEDGER TAB */}
        {tab === 'ledger' && ledger && (
          <div>
            <h3 style={s.sectionTitle}>Purchases from PO</h3>
            {ledger.purchases.length === 0 && <p style={s.empty}>No purchases yet.</p>}
            {ledger.purchases.map(p => (
              <div key={p.id} style={s.txnRow}>
                <div>
                  <p style={s.txnTitle}>{p.package_name || 'Package'} · {p.events_purchased} events</p>
                  <p style={s.txnMeta}>{new Date(p.created_at).toLocaleDateString('en-IN')}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={s.txnAmount}>₹{(p.amount_paise / 100).toFixed(0)}</p>
                  <span style={{ ...s.badge, background: p.status === 'paid' ? '#d1fae5' : '#fef3c7', color: p.status === 'paid' ? '#065f46' : '#92400e' }}>{p.status}</span>
                </div>
              </div>
            ))}

            <h3 style={{ ...s.sectionTitle, marginTop: '2rem' }}>Quota Assigned to Photographers</h3>
            {ledger.assignments.length === 0 && <p style={s.empty}>No assignments yet.</p>}
            {ledger.assignments.map(a => (
              <div key={a.id} style={s.txnRow}>
                <div>
                  <p style={s.txnTitle}>{a.photographer_name} — {a.events_delta} events</p>
                  <p style={s.txnMeta}>{a.photographer_email} · {new Date(a.created_at).toLocaleDateString('en-IN')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: '100vh', background: '#f0f4f8', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  header: { background: 'linear-gradient(135deg, #0f3460, #533483)', color: '#fff', padding: '1.5rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { margin: 0, fontSize: '1.5rem', fontWeight: 800 },
  subtitle: { margin: '0.25rem 0 0', opacity: 0.8, fontSize: '0.9rem' },
  logoutBtn: { padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', padding: '1.5rem 2rem 0' },
  statCard: { background: '#fff', borderRadius: '1rem', padding: '1.25rem', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  statValue: { fontSize: '2rem', fontWeight: 800, margin: 0 },
  statLabel: { color: '#888', fontSize: '0.75rem', margin: '0.25rem 0 0', fontWeight: 600 },
  tabs: { display: 'flex', padding: '1rem 2rem 0', gap: '0.5rem', borderBottom: '1px solid #e5e7eb', background: '#fff', marginTop: '1.5rem' },
  tab: { padding: '0.6rem 1.2rem', background: 'none', border: 'none', borderBottom: '2px solid transparent', fontWeight: 600, color: '#888', cursor: 'pointer', fontSize: '0.9rem' },
  tabActive: { borderBottomColor: '#4f46e5', color: '#4f46e5' },
  content: { padding: '1.5rem 2rem', maxWidth: 900, margin: '0 auto' },
  section: { background: '#fff', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionTitle: { margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.7rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '0.65rem 0.9rem', border: '1.5px solid #e5e7eb', borderRadius: '0.6rem', fontSize: '0.9rem', outline: 'none' },
  btn: { padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #0f3460, #533483)', color: '#fff', border: 'none', borderRadius: '0.6rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' },
  smallBtn: { padding: '0.4rem 0.8rem', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' },
  cancelBtn: { padding: '0.4rem 0.6rem', background: '#f3f4f6', color: '#555', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem' },
  txnRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6' },
  txnTitle: { margin: 0, fontWeight: 600, color: '#1a1a2e', fontSize: '0.9rem' },
  txnMeta: { margin: '0.2rem 0 0', color: '#999', fontSize: '0.8rem' },
  txnAmount: { margin: 0, fontWeight: 700, color: '#1a1a2e' },
  photoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6' },
  photoName: { margin: 0, fontWeight: 600, color: '#1a1a2e', fontSize: '0.9rem' },
  photoMeta: { margin: '0.15rem 0 0', color: '#999', fontSize: '0.8rem' },
  quotaNum: { margin: 0, fontWeight: 700, color: '#0f3460', fontSize: '1.1rem', textAlign: 'center' },
  quotaLabel: { margin: 0, color: '#999', fontSize: '0.7rem', textAlign: 'center' },
  packagesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem', marginTop: '1rem' },
  packageCard: { background: '#fff', borderRadius: '1rem', padding: '1.5rem', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '2px solid transparent' },
  pkgName: { margin: '0 0 0.5rem', fontWeight: 700, color: '#1a1a2e' },
  pkgEvents: { margin: '0 0 0.5rem', fontSize: '2rem', fontWeight: 800, color: '#0f3460' },
  pkgPrice: { margin: '0 0 0.25rem', fontSize: '1.5rem', fontWeight: 700, color: '#10b981' },
  pkgPer: { margin: 0, color: '#999', fontSize: '0.8rem' },
  badge: { display: 'inline-block', padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700 },
  empty: { color: '#999', fontSize: '0.875rem' },
  errText: { color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' },
  eventRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.875rem 0', borderBottom: '1px solid #f3f4f6' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' },
  modal: { background: '#fff', borderRadius: '1.25rem', padding: '1.75rem', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto' },
  successBox: { background: '#d1fae5', border: '1px solid #a7f3d0', borderRadius: '0.5rem', padding: '0.75rem', color: '#065f46', fontSize: '0.875rem', marginTop: '0.5rem' }
};

export default DealerDashboard;
